import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flower2, Loader2, Gem, Droplets, Apple } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";

const Remedies = () => {
  const { token } = useAuth();
  const [currentPhase, setCurrentPhase] = useState("menstrual");
  const [remedies, setRemedies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycleData, setCycleData] = useState(null);

  const phases = [
    { id: "menstrual", name: "Menstrual", color: "text-cycle-menstrual" },
    { id: "follicular", name: "Follicular", color: "text-cycle-follicular" },
    { id: "ovulation", name: "Ovulation", color: "text-cycle-ovulation" },
    { id: "luteal", name: "Luteal", color: "text-cycle-luteal" }
  ];

  useEffect(() => {
    fetchCurrentCycle();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRemedies(currentPhase);
  }, [currentPhase]);

  const fetchCurrentCycle = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/cycle/current`, { headers, withCredentials: true });
      setCycleData(res.data);
      if (res.data.phase && res.data.phase !== "unknown") {
        setCurrentPhase(res.data.phase);
      }
    } catch (error) {
      // non-critical fetch error
    }
  };

  const fetchRemedies = async (phase) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cycle/remedies/${phase}`);
      setRemedies(res.data);
    } catch (error) {
      // non-critical fetch error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="remedies-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Flower2 className="w-8 h-8 text-green-400" />
          Herbal Remedies
        </h1>
        <p className="text-mystic-200/70">Natural support for each phase of your cycle</p>
        {cycleData?.phase_name && (
          <p className="text-mystic-300 mt-2">
            You're currently in your <span className="font-medium">{cycleData.phase_name}</span>
          </p>
        )}
      </motion.div>

      <Tabs value={currentPhase} onValueChange={setCurrentPhase} className="w-full">
        <TabsList className="grid grid-cols-4 bg-mystic-800/50 p-1 rounded-xl">
          {phases.map((phase) => (
            <TabsTrigger
              key={phase.id}
              value={phase.id}
              data-testid={`tab-${phase.id}`}
              className={`data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg transition-all ${
                cycleData?.phase === phase.id ? "ring-2 ring-mystic-300" : ""
              }`}
            >
              {phase.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
          </div>
        ) : (
          remedies && phases.map((phase) => (
            <TabsContent key={phase.id} value={phase.id} className="space-y-6 mt-6">
              {/* Herbs */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Flower2 className="w-5 h-5 text-green-400" />
                      Herbs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {remedies.herbs?.map((herb, i) => (
                        <div key={`k-${i}`} className="glass-light rounded-xl p-4">
                          <h4 className="text-white font-medium mb-1">{herb.name}</h4>
                          <p className="text-mystic-200/70 text-sm mb-2">{herb.benefit}</p>
                          <p className="text-mystic-300 text-xs">{herb.usage}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Oils */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-amber-400" />
                      Essential Oils
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {remedies.oils?.map((oil, i) => (
                        <div key={`k-${i}`} className="glass-light rounded-xl p-4">
                          <h4 className="text-white font-medium mb-1">{oil.name}</h4>
                          <p className="text-mystic-200/70 text-sm mb-2">{oil.benefit}</p>
                          <p className="text-mystic-300 text-xs">{oil.usage}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Crystals */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Gem className="w-5 h-5 text-mystic-300" />
                      Crystals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {remedies.crystals?.map((crystal, i) => (
                        <div key={`k-${i}`} className="glass-light rounded-xl p-4">
                          <h4 className="text-white font-medium mb-1">{crystal.name}</h4>
                          <p className="text-mystic-200/70 text-sm mb-2">{crystal.benefit}</p>
                          <p className="text-mystic-300 text-xs">Placement: {crystal.placement}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Foods */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Apple className="w-5 h-5 text-red-400" />
                      Nutrition
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="glass-light rounded-xl p-4">
                        <h4 className="text-green-400 font-medium mb-2">Foods to Embrace</h4>
                        <ul className="space-y-1">
                          {remedies.foods?.map((food, i) => (
                            <li key={`k-${i}`} className="text-mystic-200/80 text-sm flex items-center gap-2">
                              <span className="text-green-400">✓</span> {food}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="glass-light rounded-xl p-4">
                        <h4 className="text-red-400 font-medium mb-2">Foods to Limit</h4>
                        <ul className="space-y-1">
                          {remedies.avoid?.map((food, i) => (
                            <li key={`k-${i}`} className="text-mystic-200/80 text-sm flex items-center gap-2">
                              <span className="text-red-400">✗</span> {food}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </TabsContent>
          ))
        )}
      </Tabs>
    </div>
  );
};

export default Remedies;
